import { Modal, Paper, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import { Icon } from '@/icon/Icon';
import Image, { ImageProps } from './Image';

const ModalPaper = styled(Paper, {
  name: 'ImageModal',
  slot: 'Paper',
})(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  outline: 0,
  padding: theme.spacing(2),
}));

const CloseButton = styled('div', {
  name: 'ImageModal',
  slot: 'CloseButton',
})(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
}));

const ImageButton = styled('div', {
  name: 'ImageModal',
  slot: 'ImageButton',
})({
  cursor: 'pointer',
  display: 'inline-block',
  position: 'relative',
  padding: 0,
  width: '100%',
});

const DeleteButton = styled('div', {
  name: 'ImageModal',
  slot: 'DeleteButton',
})(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(5),
}));

const ImageModalIcon = styled(Icon, {
  name: 'ImageModal',
  slot: 'Icon',
})(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  cursor: 'pointer',
  ...getOverlayIconButtonStyle(theme),
  borderRadius: '100%',
  padding: theme.spacing(0.5),
  height: theme.spacing(4),
  width: theme.spacing(4),
}));

export const getOverlayIconButtonStyle = (theme: Theme) => ({
  backgroundColor:
    theme.palette.mode === 'light' ? 'rgba(255,255,255,.5)' : 'rgba(0,0,0,.3)',
  color: theme.palette.text.secondary,
  '&:hover': { backgroundColor: theme.palette.getGreyOpaque(30) },
});

interface ImageModalProps extends ImageProps {}

const ImageModal = (props: ImageModalProps) => {
  const [localOpen, setLocalOpen] = useState(false);
  const open = props.open !== undefined ? props.open : localOpen;
  const setOpen = props.setOpen !== undefined ? props.setOpen : setLocalOpen;
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const onClose = () => setOpen(false);

  return (
    <>
      <ImageButton onClick={() => setOpen(true)} role="button">
        {!props.modalOverlay && <ImageModalIcon name="zoom_in" />}
        {props.onDelete && (
          <DeleteButton
            onClick={(event: MouseEvent) => {
              if (!deleteInProgress) {
                setDeleteInProgress(true);
                props.onDelete && props.onDelete();
              }
              event.stopPropagation();
            }}
            role="button"
          >
            <ImageModalIcon name="close" />
          </DeleteButton>
        )}
        <Image {...props} />
      </ImageButton>

      <ModalImage open={open} onClose={onClose} {...props} />
    </>
  );
};

export default ImageModal;

interface ModalImageProps extends ImageModalProps {
  open: boolean;
  onClose: () => void;
}

export const ModalImage = ({
  open,
  onClose,
  modalOverlay,
  ...props
}: ModalImageProps) => (
  <Modal open={open} onClose={onClose}>
    <ModalPaper>
      {modalOverlay}
      {!modalOverlay && (
        <CloseButton onClick={onClose} role="button">
          <ImageModalIcon name="close" />
        </CloseButton>
      )}
      <Image
        {...props}
        size="original"
        square={false}
        sx={{ maxWidth: '90vw', maxHeight: '90vh' }}
        isLoading={false}
      />
    </ModalPaper>
  </Modal>
);
